<template>
  <div
    class="file-upload-dnd"
    :class="[files.length > 0 && 'has-file', disabled && 'disabled', rootClass]"
  >
    <div class="text-center" v-show="!disabled">
      <template v-if="!files.length">
        <div class="mb-4">
          <v-icon>$upload</v-icon>
        </div>
        <h5 class="file-upload-dnd--title mb-2">{{ title }}</h5>
      </template>
      <p class="file-upload-dnd--description ma-0">
        drag a {{ acceptedFileNames }} file here or
        <a
          :disabled="disabled"
          :class="!disabled && 'text-decoration-underline'"
          class="dropzone-clickable"
          >browse</a
        >.
      </p>
    </div>

    <p
      v-show="disabled && !files.length"
      class="text-center file-upload-dnd--description ma-0"
    >
      No file was uploaded
    </p>

    <div class="file-upload-dnd--preview" :class="!disabled && 'mt-6'">
      <div
        v-for="({
          fileType,
          fileName,
          _progress,
          fileSize,
          fileSizeStr,
          bytesSent,
          bytesSentStr,
          status,
          fileStatus,
          accepted,
          id,
          thumbnail
        },
        indx) of files"
        :key="id"
        class="d-flex align-center"
        :class="indx !== 0 && 'mt-4'"
      >
        <div class="file-type mr-4" :class="!thumbnail && 'no-thumbnail'">
          <div class="thumbnail">
            <img v-if="thumbnail" :src="thumbnail" :alt="fileName" />
          </div>
          <div class="text">
            {{ fileType }}
          </div>
        </div>

        <div class="flex-grow-1">
          <div class="d-flex justify-space-between mb-1">
            <span>{{ fileName }}</span>

            <span class="file-status">
              <template v-if="status === 'error'">
                {{ fileStatus }}
              </template>
              <template v-else-if="status === 'success'">Complete</template>
              <template v-else>
                <span v-html="bytesSentStr" />/<span v-html="fileSizeStr" />
              </template>
            </span>
          </div>
          <v-progress-linear
            :value="_progress || (bytesSent * 100) / fileSize"
            :color="status === 'error' ? 'error' : 'success'"
            rounded
            height="8"
            background-color="secondary"
          ></v-progress-linear>
        </div>
        <div class="file-actions ml-4">
          <v-btn icon @click="remove(id)" :disabled="disabled">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <!-- invalid files are not reuuploadable -->
          <v-btn
            icon
            v-if="status === 'error' && accepted !== false"
            @click="reupload(id)"
            :disabled="disabled"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </div>
        <!-- end of v-for -->
      </div>
      <!-- end of file-upload-dnd--preview  -->
    </div>
  </div>
</template>

<script>
import BaseFileUpload from "@/mixins/base-fileupload";
import vuetifyValidationMixin from "@/mixins/has-vuetify-validations";
import { getFileExtension } from "@/services/regex";
import VInput from "vuetify/lib/components/VInput/VInput.js";

export default {
  name: "FileUploadDnd",
  mixins: [BaseFileUpload, vuetifyValidationMixin],
  extends: VInput,
  props: {
    title: {
      type: String,
      default: "File Upload"
    },
    multiple: Boolean,
    value: Array
  },
  created() {
    this.dragNDrop = true;
  },
  data: () => ({
    files: []
  }),
  watch: {
    value: {
      immediate: true,
      handler(files) {
        //if files array doesnt exist or there is nothing in it and
        if ((!files || !files.length) && this.files.length) {
          // if the value change(clearing) is not a result of confirmed clear up, we delete our queue, otherwise we skip to allow staged file being processed
          if (this.existingFilesConfirmingState !== "CONFIRMING")
            this.files = [];
          return;
        }

        if (files && files.every(({ id, fileName }) => id && fileName)) {
          // these are existing files
          this.files = files.map(({ id, fileName }) => ({
            id,
            uploadId: id,
            fileName,
            fileType: getFileExtension(fileName),
            status: "success",
            _progress: 100
          }));
        }
      }
    }
  },
  methods: {
    addFile(file, dzFile) {
      this.files.push(file);
      // intentionally non reactive
      file.dzFile = dzFile;
    },
    removeAllKeeping(...files) {
      for (let i = 0; i < this.files.length; i++) {
        const file = this.files[i];
        if (!files.includes(file)) {
          this.remove(file.id);
          // array is being reindexed after elements are deleted. we decrease i by 1 to account for that to avoid skipping elements
          i--;
        }
      }
    },
    remove(_id) {
      const indx = this.files.findIndex(({ id }) => id === _id);
      if (indx > -1) {
        const [file] = this.files.splice(indx, 1);

        if (
          this.isExistingFile(file) &&
          // for CONFIRMING, DONE is set externally
          !["DONE", "CONFIRMING"].includes(this.existingFilesConfirmingState)
        ) {
          // the user has removed an existing file manually, so no need to confirm anymore
          this.existingFilesConfirmingState = "DONE";
        }
        // file.dzFile might be undefined when an existing file is rendered and removed after initialization
        file.dzFile && this.dropzone.removeFile(file.dzFile);
      }
    },
    isExistingFile(file) {
      return !!file._progress;
    },
    reupload(id) {
      const dropzone = this.dropzone;
      const file = dropzone.files.find(file => file.upload.uuid === id);
      // Dropzone.ADDED is 'added', using string to avoid import the library
      file.status = "added";
      dropzone.enqueueFile(file);
    }
  }
};
</script>

<style lang="sass" scoped>
.file-upload-dnd
  border-radius: 2rem
  background-color: rgba($howmany-grey, 0.3)
  border: 3px dashed rgba(#AAA, 0.3)
  padding: 3rem
  max-width: 560px
  color: $darker-grey
  &.dz-drag-hover
    background-color: rgba($primary, 0.1) !important
  &.disabled
    background-color: rgba(black, 0.06) !important
  &.has-file
    background-color: rgba(white, 0.3)
    padding: 2rem
  &--title, &--description
    font-weight: normal
    font-size: 20px
  &--preview
    &:empty
      display: none
    .file-type
      position: relative
      .thumbnail
        width: 40px
        height: 40px
        border-radius: 0.5rem
        overflow: hidden
      .text
        position: absolute
        display: flex
        top: 0
        left: 0
        width: 100%
        height: 100%
        justify-content: center
        align-items: center
        color: white
        opacity: 0
        transition: opacity 0.2s linear
        text-transform: uppercase
        &.full-opacity
          opacity: 1
      &:hover, &.no-thumbnail
        .thumbnail
          background: $lightish-grey
          img
            display: none
        .text
          opacity: 1
    .file-status
      font-size: 12px
      color: $subtitle
    // I forgot why we need a larger than necessary width for this. It seems it looks nicer without it, so comment it out for now
    //.file-actions
    //  width: 72px
</style>
